import {
	type MatchResultBody,
	startSocialMatch as requestSocialMatchStart,
	getSocialMatchStatus as requestSocialMatchStatus,
	submitSocialMatch as requestSocialMatchSubmit,
	type SocialMatchReason,
	type SocialMatchStartResponse,
	type SocialMatchStatusResponse,
	type SocialMatchSubmitResponse,
	type SocialPendingFeedback,
	socialMatchConfirm,
	socialMatchFeedback,
	socialMatchFeedbackSkip,
	socialMatchLater,
	socialMatchOpen,
	socialMatchSkip,
} from "@/shared/api/generated-social"

export type SocialMatchStatus = SocialMatchStatusResponse
export type SocialMatchRequestStatus = SocialMatchStartResponse["status"]
export type SocialMatchPendingFeedback = NonNullable<SocialPendingFeedback>
// «Повод познакомиться»: только заполненные виды, от 1 до 3 ключей.
export type SocialMatchRequestReason = NonNullable<SocialMatchReason>

export async function getSocialMatchStatus(): Promise<SocialMatchStatus> {
	return requestSocialMatchStatus()
}

export async function submitSocialMatchFeedback(args: {
	requestId: string
	rating: number
	comment: string
}): Promise<SocialMatchStatus> {
	return socialMatchFeedback({
		body: {
			request_id: args.requestId,
			rating: args.rating,
			comment: args.comment,
		},
	})
}

export async function skipSocialMatchFeedback(
	requestId: string,
): Promise<SocialMatchStatus> {
	return socialMatchFeedbackSkip({
		body: { request_id: requestId },
	})
}

// Гейт перед welcome-экраном. При блокировке бэк отдаёт `status`
// (search_blocked_no_message / search_blocked_pending) и сам эмитит WS-уведомление.
export async function startSocialMatch(
	userId: number,
): Promise<SocialMatchStartResponse> {
	return requestSocialMatchStart({
		body: { user_id: userId },
	})
}

// `reason` — поводы познакомиться или null, если юзер не заполнил ни одного.
// Поле обязательное.
export async function submitSocialMatch(
	userId: number,
	args: { request: string; reason: SocialMatchReason },
): Promise<SocialMatchSubmitResponse> {
	return requestSocialMatchSubmit({
		body: {
			user_id: userId,
			request: args.request,
			reason: args.reason,
		},
	})
}

// The client sends each social match choice as a POST request, not as a
// `notification.response` frame. `notification_id` is the key of the proposal.

// «Пропустить» — кандидат не попадёт ни в «Новые», ни в ЧС. Ветки в UI нет:
// на обоих исходах попап закрывается, цель скипа достигнута.
export async function skipSocialMatchCandidate(
	userId: number,
	notificationId: number,
) {
	await socialMatchSkip({
		body: {
			user_id: userId,
			notification_id: notificationId,
		},
	})
}

// «В чат!» кандидата — принятие social-мэтча; expired = предложение уже
// отозвано/отвечено — фронт показывает «не актуально» вместо чата.

export async function confirmSocialMatchCandidate(
	userId: number,
	notificationId: number,
): Promise<MatchResultBody["result"]> {
	const { result } = await socialMatchConfirm({
		body: {
			user_id: userId,
			notification_id: notificationId,
		},
	})
	return result
}

// Попап «Я нашёл твой Social Мэтч» — у заказчика и instant-кандидата. Мэтч уже
// состоялся, состояние эти команды не меняют: они нужны ровно затем, чтобы
// узнать, жив ли он ещё. Прежний notification.response со status "closed" /
// "opened" ответа не давал, и попап не замечал, что вторая сторона отклонила.
export async function postponeSocialMatchFound(
	userId: number,
	notificationId: number,
): Promise<MatchResultBody["result"]> {
	const { result } = await socialMatchLater({
		body: {
			user_id: userId,
			notification_id: notificationId,
		},
	})
	return result
}

export async function openSocialMatchFound(
	userId: number,
	notificationId: number,
): Promise<MatchResultBody["result"]> {
	const { result } = await socialMatchOpen({
		body: {
			user_id: userId,
			notification_id: notificationId,
		},
	})
	return result
}
